import { ActionTree } from 'vuex';
import { EFieldAction } from '../fields/store/field.constants';
import { DocumentService } from '@/api/documents/services/document.service';
import { IImplementResourceDTO } from '@/api/resources/dtos/implement-resource-dto';
import { ResourceService } from '@/api/resources/services/resource.service';
import { IDocumentResource } from '@/api/resources/types/document-resource';
import { VideoService } from '@/api/video/services/video.service';
import { IUpdateDocument } from '@/document-edit/assembler/document/types/update-document';
import { AssemblerHelper } from '@/document-edit/assembler/helpers/assembler.helper';
import {
  ERecipientAction,
  recipientNamespace,
} from '@/document-edit/assembler/recipients/store/recipient.constants';
import { ERecipientRole } from '@lib-shared/recipients/types/enums/recipient-role';
import { RecipientService } from '@/api/recipients/services/recipient.service';
import {
  EAssemblerAction,
  EAssemblerGetter,
  EAssemblerMutation,
  getDefaultState,
} from '@/document-edit/assembler/store/assembler.constants';
import { IAssemblerState } from '@/document-edit/assembler/store/assembler.types';
import { EDocumentPageType } from '@lib-shared/enums/document-page-type';
import { EExternalEditorType } from '@/enums/ExternalEditorType';
import { EFieldSource } from '@lib-shared/fields/types/enums/field-source';
import { IField } from '@lib-shared/fields/types/field';
import { translate } from '@/helpers/translation.helper';
import { RecipientHelper } from '@/recipients/recipient.helper';
import { RootState } from '@/store/store';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentAttachment } from '@lib-shared/types/document-attachment';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { IRecipient } from '@lib-shared/types/Recipient';
import { ITag } from '@lib-shared/types/Tag';
import { IUser } from '@/types/User';
import { EnumDocumentType } from '@/enums/DocumentType';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import {
  documentEditNamespace,
  EDocumentEditAction,
} from '@/document-edit/store/document-edit.constants';
import { AttachmentService } from '@/api/attachments/services/attachment.service';
import { IEngagementVideo } from '@lib-shared/types/engagement-video';
import { DocumentManipulatorHelper } from '@/document-manipulator/helpers/document-manipulator.helper';
import { TagService } from '@/api/tags/services/tag.service';
import { ETagAction, tagsModuleName } from '@/tags/store/tag.constants';
import bugsnagClient from '@lib-shared/bugsnag';

const couldNotUpdateMessage = 'could-not-update-document';

export const actions: ActionTree<IAssemblerState, RootState> = {
  async [EAssemblerAction.LoadDocument]({ dispatch, commit }, documentId: string) {
    try {
      commit(EAssemblerMutation.SetLoading, true);
      const document: IDocument = await DocumentService.loadDocument(documentId);
      dispatch(EAssemblerAction.LoadDocumentSuccess, document);
    } catch (error) {
      dispatch(EAssemblerAction.LoadDocumentFailed, { error, payload: { documentId } });
    }
  },
  [EAssemblerAction.LoadDocumentSuccess]({ commit, dispatch }, document: IDocument) {
    commit(EAssemblerMutation.SetDocument, document);
    commit(EAssemblerMutation.SetLoading, false);
    commit(EAssemblerMutation.SetLoaded, true);
    dispatch(EAssemblerAction.LoadDocumentResources);
  },
  [EAssemblerAction.LoadDocumentFailed]({ dispatch, commit }) {
    commit(EAssemblerMutation.SetLoading, false);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('failed-to-load-document'), {
      root: true,
    });
  },
  [EAssemblerAction.ShowEditDocumentPage](
    { commit },
    { isInEditMode, pageId }: { isInEditMode: boolean; pageId: string }
  ) {
    commit(EAssemblerMutation.ShowEditDocumentPage, { isInEditMode, pageId });
  },
  [EAssemblerAction.RemovePage]({ dispatch, commit, state }, page: IDocumentPage) {
    const originalPages: IDocumentPage[] = state.document.pages;
    const filteredPages: IDocumentPage[] = DocumentManipulatorHelper.filterPage(
      page,
      state.document.pages
    );
    dispatch(EAssemblerAction.SetPages, filteredPages);
    if (!filteredPages.length) {
      dispatch(EAssemblerAction.UpdateDocument, {
        name: '',
        externalEditorId: '',
        externalEditorType: EExternalEditorType.None,
      });
      return;
    }
    const deletedPageIndex: number = originalPages.findIndex(
      (originalPage: IDocumentPage) => originalPage.id === page.id
    );
    const currentDocumentPage: IDocumentPage = AssemblerHelper.getCurrentDocumentPageAfterPageDelete(
      deletedPageIndex,
      filteredPages
    );
    commit(EAssemblerMutation.SetCurrentDocumentPage, currentDocumentPage);
  },
  [EAssemblerAction.ResetState]({ commit }) {
    commit(EAssemblerMutation.SetState, getDefaultState());
  },
  [EAssemblerAction.ShowDocumentSettings]({ commit }, show: boolean) {
    commit(EAssemblerMutation.ShowDocumentSettings, show);
  },
  async [EAssemblerAction.UpdateDocument]({ dispatch, commit, state }, payload: IUpdateDocument) {
    const previousDocument: IDocument = { ...state.document };
    const unsignableDocument: boolean = [
      EnumDocumentType.Introduction,
      EnumDocumentType.Presentation,
    ].includes(payload.type);

    if (unsignableDocument && state.document?.recipients?.length) {
      const recipientsToUpdate: IRecipient[] = AssemblerHelper.getSignableRecipients(
        state.document.recipients
      );
      recipientsToUpdate.forEach((recipient: IRecipient) =>
        dispatch(EAssemblerAction.UpdateRecipient, {
          id: recipient.id,
          properties: { role: ERecipientRole.CC },
        })
      );
    }

    try {
      const updatedDocument: IDocument = { ...state.document, ...payload };
      commit(EAssemblerMutation.SetDocument, updatedDocument);
      await DocumentService.updateDocument(payload, state.document.id);
      dispatch(EAssemblerAction.ShowDocumentSettings, false);
    } catch (error) {
      dispatch(EAssemblerAction.UpdateDocumentFailed, { previousDocument });
    }
  },
  [EAssemblerAction.UpdateDocumentFailed](
    { dispatch, commit },
    { previousDocument }: { previousDocument: IDocument }
  ) {
    commit(EAssemblerMutation.SetDocument, previousDocument);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate(couldNotUpdateMessage), {
      root: true,
    });
  },
  [EAssemblerAction.SetCurrentDocumentPage]({ commit }, documentPage: IDocumentPage) {
    commit(EAssemblerMutation.SetCurrentDocumentPage, documentPage);
  },
  [EAssemblerAction.SetSelectedUser]({ commit }, user: IUser) {
    commit(EAssemblerMutation.SetSelectedRecipient, {
      id: null,
      connectFirstRecipient: false,
    });
    commit(EAssemblerMutation.SetSelectedUser, user);
  },
  [EAssemblerAction.SetSelectedRecipient](
    { commit },
    {
      recipient,
      connectFirstRecipient,
    }: {
      recipient?: IRecipient;
      connectFirstRecipient: boolean;
    }
  ) {
    commit(EAssemblerMutation.SetSelectedUser, null);
    commit(EAssemblerMutation.SetSelectedRecipient, {
      id: recipient ? recipient.id : null,
      connectFirstRecipient,
    });
  },
  async [EAssemblerAction.RemoveRecipient]({ state, commit, dispatch }, recipientId: string) {
    const existingRecipients: IRecipient[] = state.document.recipients;
    let remainingRecipients: IRecipient[] = existingRecipients.filter(
      (recipient: IRecipient) => recipient.id !== recipientId
    );
    if (remainingRecipients.length > 1) {
      remainingRecipients = RecipientHelper.fixRecipientsGaps(remainingRecipients);
    }
    try {
      commit(EAssemblerMutation.SetRecipients, remainingRecipients);
      const hasSigner: boolean = state.document.recipients.some(
        (recipient: IRecipient) => recipient.role === ERecipientRole.Signer
      );

      const hasApprover: boolean = state.document.recipients.some(
        (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
      );

      if (!hasSigner && hasApprover) {
        dispatch(EAssemblerAction.UpdateRecipientApproversToCC);
      }
      await RecipientService.deleteRecipient(recipientId);
      if (remainingRecipients.length === 1) {
        dispatch(EAssemblerAction.TurnOffSigningOrder);
      }
      await dispatch(EAssemblerAction.RemoveRecipientSuccess, recipientId);
    } catch (error) {
      dispatch(EAssemblerAction.RemoveRecipientFailed, existingRecipients);
    }
  },
  [EAssemblerAction.RemoveRecipientSuccess]({ state, dispatch }, recipientId: string) {
    dispatch(`fields/${EFieldAction.Load}`, state.document.id);
    if (state.selectedRecipient.id === recipientId) {
      dispatch(EAssemblerAction.SetSelectedRecipient, {
        id: null,
        connectFirstRecipient: false,
      });
      dispatch(EAssemblerAction.SetSelectedUser, state.document.user);
    }
  },
  [EAssemblerAction.RemoveRecipientFailed]({ commit }, oldRecipients: IRecipient[]) {
    commit(EAssemblerMutation.SetRecipients, oldRecipients);
  },
  async [EAssemblerAction.AddNewRecipient](
    { dispatch, state },
    {
      recipient,
      documentId,
      file,
    }: {
      recipient: IRecipient;
      documentId: string;
      file: File;
    }
  ) {
    try {
      dispatch(EAssemblerAction.SetRecipientModalIsLoading, true);
      let newRecipient = recipient;
      if (file) {
        const thumbUrl = await RecipientService.updateRecipientThumbnail(file, recipient.email);
        newRecipient = { ...newRecipient, thumbUrl };
      }
      const recipientId: string = await RecipientService.saveRecipient(newRecipient, documentId);
      await RecipientService.updateRecipient(recipientId, newRecipient);

      await dispatch(EAssemblerAction.LoadRecipients);
      if (recipient.role === ERecipientRole.Approver) {
        if (!state.document.isSigningOrder) {
          await dispatch(EAssemblerAction.TurnOnSigningOrder);
        }
        const sortedRecipients = AssemblerHelper.sortRecipients(state.document.recipients);

        const recipientOrder: Partial<IRecipient>[] = sortedRecipients.map(
          (recipient: IRecipient, index: number) => ({
            id: recipient.id,
            orderNum: index + 1,
          })
        );
        await RecipientService.updateRecipientOrder(documentId, recipientOrder);
      }
      dispatch(EAssemblerAction.RefreshRecipients, { documentId });

      dispatch(`${recipientNamespace}/${ERecipientAction.SetModalVisibility}`, false, {
        root: true,
      });
      dispatch(EAssemblerAction.SetRecipientModalIsLoading, false);
    } catch (error) {
      dispatch(EAssemblerAction.CreateRecipientFailed, error);
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-add-recipient'),
        {
          root: true,
        }
      );
      dispatch(EAssemblerAction.SetRecipientModalIsLoading, false);
    }
  },
  async [EAssemblerAction.UpdateRecipientLegacy](
    { dispatch, state },
    {
      recipient,
      documentId,
      file,
    }: {
      recipient: IRecipient;
      documentId: string;
      file: File;
    }
  ) {
    let oldRecipient: IRecipient = {};
    if (recipient.id) {
      oldRecipient = state.document.recipients.find(
        (stateRecipient: IRecipient) => stateRecipient.id === recipient.id
      );
    }
    try {
      dispatch(EAssemblerAction.SetRecipientModalIsLoading, true);
      let newRecipient = recipient;
      if (file) {
        const thumbUrl = await RecipientService.updateRecipientThumbnail(file, recipient.email);
        newRecipient = { ...newRecipient, thumbUrl };
      }
      const recipientId: string = await RecipientService.saveRecipient(newRecipient, documentId);
      await RecipientService.updateRecipient(recipientId, newRecipient);
      if (recipient.role === ERecipientRole.Approver) {
        await dispatch(EAssemblerAction.TurnOnSigningOrder);

        const sortedRecipients = AssemblerHelper.sortRecipients(state.document.recipients);
        const recipientOrder: Partial<IRecipient>[] = sortedRecipients.map(
          (recipient: IRecipient) => ({
            id: recipient.id,
            orderNum: recipient.orderNum,
          })
        );

        await RecipientService.updateRecipientOrder(documentId, recipientOrder);
      }
      if (
        oldRecipient.role !== ERecipientRole.Approver &&
        recipient.role === ERecipientRole.Approver &&
        recipient.orderNum !== 1
      ) {
        dispatch(EAssemblerAction.UpdateRecipientsSigningOrder, {
          oldIndex: recipient.orderNum - 1,
          newIndex: 0,
          drag: true,
        });
      }
      dispatch(EAssemblerAction.RefreshRecipients, { documentId });
      dispatch(`${recipientNamespace}/${ERecipientAction.SetModalVisibility}`, false, {
        root: true,
      });
      dispatch(EAssemblerAction.SetRecipientModalIsLoading, false);
    } catch (error) {
      dispatch(EAssemblerAction.UpdateRecipientFailed, oldRecipient);
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-update-recipient'),
        {
          root: true,
        }
      );
      dispatch(EAssemblerAction.SetRecipientModalIsLoading, false);
    }
  },
  [EAssemblerAction.SetRecipientModalIsLoading]({ dispatch }, isLoading: boolean) {
    dispatch(`${recipientNamespace}/${ERecipientAction.SetIsLoading}`, isLoading, {
      root: true,
    });
  },
  [EAssemblerAction.RefreshRecipients]({ dispatch }, { documentId }: { documentId: string }) {
    dispatch(EAssemblerAction.LoadRecipients);
    dispatch(EAssemblerAction.LoadDocumentAttachments, { documentId });
  },
  async [EAssemblerAction.CreateRecipient](
    { dispatch },
    { contactId, documentId, role }: { contactId: string; documentId: string; role: ERecipientRole }
  ) {
    try {
      const recipient: IRecipient = await RecipientService.createRecipient(
        contactId,
        documentId,
        role
      );
      dispatch(EAssemblerAction.CreateRecipientSuccess, { recipient, documentId });
    } catch (error) {
      dispatch(EAssemblerAction.CreateRecipientFailed, error);
    }
  },
  [EAssemblerAction.CreateRecipientSuccess](
    { commit, dispatch, state },
    { recipient, documentId }: { recipient: IRecipient; documentId: string }
  ) {
    if (recipient.role === ERecipientRole.Approver) {
      dispatch(EAssemblerAction.TurnOnSigningOrder);
    }
    if (state.document.isSigningOrder) {
      const lastRecipient: IRecipient =
        state.document?.recipients[state.document.recipients.length - 1];
      const orderNum: number = lastRecipient ? lastRecipient.orderNum + 1 : 1;
      commit(EAssemblerMutation.AddRecipient, { ...recipient, orderNum });
      dispatch(EAssemblerAction.UpdateRecipient, {
        id: recipient.id,
        properties: { orderNum },
      });
    } else {
      commit(EAssemblerMutation.AddRecipient, recipient);
    }
    dispatch(EAssemblerAction.RefreshRecipients, { documentId });
    dispatch(EAssemblerAction.ConnectFirstRecipientToFields);
  },
  [EAssemblerAction.ConnectFirstRecipientToFields]({ dispatch, state, getters }) {
    const firstRecipient: IRecipient = getters[EAssemblerGetter.FirstAddedRecipient];
    const fieldsWithMissingRecipient: IField[] = state['fields'].fields.filter(
      (field: IField) => field.source === EFieldSource.Recipient && !field.recipientId
    );

    if (firstRecipient && !!fieldsWithMissingRecipient.length) {
      const updatedFields: IField[] = fieldsWithMissingRecipient.map((field: IField) => {
        return {
          ...field,
          recipientId: firstRecipient.id,
        };
      });

      if (getters[EAssemblerGetter.ConnectFirstRecipient]) {
        dispatch(EAssemblerAction.SetSelectedRecipient, {
          recipient: firstRecipient,
          connectFirstRecipient: false,
        });
      }
      dispatch(`fields/${EFieldAction.Update}`, updatedFields);
    }
  },
  [EAssemblerAction.TurnOnSigningOrder]({ dispatch, state }) {
    dispatch(EAssemblerAction.UpdateDocument, { ...state.document, isSigningOrder: 1 });
  },
  [EAssemblerAction.TurnOffSigningOrder]({ dispatch, state }) {
    dispatch(EAssemblerAction.UpdateDocument, { ...state.document, isSigningOrder: 0 });
  },
  async [EAssemblerAction.UpdateRecipient](
    { commit, dispatch, state },
    { id, properties }: { id: string; properties: Partial<IRecipient> }
  ) {
    const oldRecipient: IRecipient = state.document.recipients.find(
      (recipient: IRecipient) => recipient.id === id
    );
    const updatedRecipient: IRecipient = { ...oldRecipient, ...properties };
    commit(EAssemblerMutation.UpdateRecipient, updatedRecipient);

    const hasSigner: boolean = state.document.recipients.some(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Signer
    );

    const hasApprover: boolean = state.document.recipients.some(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
    );

    if (!hasSigner && hasApprover) {
      dispatch(EAssemblerAction.UpdateRecipientApproversToCC);
    }
    try {
      await RecipientService.updateRecipient(id, properties);
      if (updatedRecipient.role === ERecipientRole.Approver) {
        await dispatch(EAssemblerAction.TurnOnSigningOrder);
      }
      if (
        oldRecipient.role !== ERecipientRole.Approver &&
        updatedRecipient.role === ERecipientRole.Approver &&
        updatedRecipient.orderNum !== 1
      ) {
        dispatch(EAssemblerAction.UpdateRecipientsSigningOrder, {
          oldIndex: updatedRecipient.orderNum - 1,
          newIndex: 0,
          drag: true,
        });
      }
      dispatch(`${recipientNamespace}/${ERecipientAction.SetModalVisibility}`, false, {
        root: true,
      });
    } catch (error) {
      dispatch(EAssemblerAction.UpdateRecipientFailed, oldRecipient);
    }
  },
  [EAssemblerAction.UpdateRecipientApproversToCC]({ dispatch, state, commit }) {
    const approverRecipients: IRecipient[] = state.document.recipients.filter(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
    );

    approverRecipients.forEach(async (oldRecipient: IRecipient) => {
      const updatedRecipient: IRecipient = { ...oldRecipient, role: ERecipientRole.CC };
      commit(EAssemblerMutation.UpdateRecipient, updatedRecipient);
      try {
        await RecipientService.updateRecipient(oldRecipient.id, { role: ERecipientRole.CC });
      } catch {
        dispatch(EAssemblerAction.UpdateRecipientFailed, oldRecipient);
      }
    });
  },
  [EAssemblerAction.UpdateRecipientFailed]({ commit }, oldRecipient: IRecipient) {
    commit(EAssemblerMutation.UpdateRecipient, oldRecipient);
  },
  async [EAssemblerAction.DeleteAttachment](
    { commit, dispatch, state },
    { documentAttachment }: { documentAttachment: IDocumentAttachment }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      const updatedDocumentAttachments = originalDocumentAttachments.filter(
        (currentAttachment: IDocumentAttachment) => currentAttachment.id !== documentAttachment.id
      );
      commit(EAssemblerMutation.UpdateDocumentAttachment, updatedDocumentAttachments);

      const response: boolean = await AttachmentService.delete(documentAttachment.id);
      if (!response) {
        dispatch(EAssemblerAction.DeleteAttachmentFailed, originalDocumentAttachments);
      }
    } catch (e) {
      dispatch(EAssemblerAction.DeleteAttachmentFailed, originalDocumentAttachments);
    }
  },

  [EAssemblerAction.DeleteAttachmentFailed](
    { dispatch, commit },
    originalDocumentAttachments: IDocumentAttachment[]
  ) {
    commit(EAssemblerMutation.UpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-delete-attachment'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.RestrictAccessAttachment](
    { dispatch, state, commit },
    {
      documentAttachment,
      recipientIds,
    }: {
      documentAttachment: IDocumentAttachment;
      recipientIds: string[];
    }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      if (recipientIds.length > 0) {
        const updatedDocumentAttachments = originalDocumentAttachments.map(
          (currentAttachment: IDocumentAttachment) => {
            if (currentAttachment.id === documentAttachment.id) {
              currentAttachment.accessRecipientList = state.document.recipients.filter(
                (recipient: IRecipient) => recipientIds.indexOf(recipient.id) > -1
              );
              currentAttachment.restrictAccess = true;
            }
            return currentAttachment;
          }
        );
        commit(EAssemblerMutation.UpdateDocumentAttachment, updatedDocumentAttachments);

        const response: boolean = await AttachmentService.setRestrictedAccess(
          documentAttachment.id,
          recipientIds
        );
        if (!response) {
          dispatch(EAssemblerAction.RestrictAccessAttachmentFailed, originalDocumentAttachments);
        }
      } else {
        dispatch(EAssemblerAction.RemoveRestrictedAccessAttachment, {
          documentAttachment,
        });
      }
    } catch (e) {
      dispatch(EAssemblerAction.RestrictAccessAttachmentFailed, originalDocumentAttachments);
    }
  },
  [EAssemblerAction.RestrictAccessAttachmentFailed](
    { dispatch, commit },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(EAssemblerMutation.UpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-update-restricted-access-for-attachment'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.RemoveRestrictedAccessAttachment](
    { dispatch, state, commit },
    { documentAttachment }: { documentAttachment: IDocumentAttachment }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      const updatedDocumentAttachments = originalDocumentAttachments.map(
        (currentAttachment: IDocumentAttachment) => {
          if (currentAttachment.id === documentAttachment.id) {
            currentAttachment.restrictAccess = false;
            currentAttachment.accessRecipientList = [];
          }
          return currentAttachment;
        }
      );
      commit(EAssemblerMutation.UpdateDocumentAttachment, updatedDocumentAttachments);
      const response: boolean = await AttachmentService.removeRestrictedAccess(
        documentAttachment.id
      );
      if (!response) {
        dispatch(EAssemblerAction.RemoveRestrictedAccessAttachmentFailed, 'error');
      }
    } catch (e) {
      dispatch(EAssemblerAction.RemoveRestrictedAccessAttachmentFailed, e);
    }
  },
  [EAssemblerAction.RemoveRestrictedAccessAttachmentFailed](
    { dispatch, commit },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(EAssemblerMutation.UpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-remove-restricted-access-from-attachment'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.RequireViewAttachment](
    { dispatch, state, commit },
    { documentAttachment }: { documentAttachment: IDocumentAttachment }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      const updatedDocumentAttachments = originalDocumentAttachments.map(
        (currentAttachment: IDocumentAttachment) => {
          if (currentAttachment.id === documentAttachment.id) {
            currentAttachment.requireView = !currentAttachment.requireView;
          }
          return currentAttachment;
        }
      );
      commit(EAssemblerMutation.UpdateDocumentAttachment, updatedDocumentAttachments);
      const response: boolean = await AttachmentService.setRequireView(
        documentAttachment.id,
        documentAttachment.requireView
      );
      if (!response) {
        dispatch(EAssemblerAction.RequireViewAttachmentFailed, originalDocumentAttachments);
      }
    } catch (e) {
      dispatch(EAssemblerAction.RequireViewAttachmentFailed, originalDocumentAttachments);
    }
  },
  [EAssemblerAction.RequireViewAttachmentFailed](
    { dispatch, commit },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(EAssemblerMutation.UpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-update-attachment'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.LoadDocumentAttachments](
    { dispatch, state },
    { documentId }: { documentId: string }
  ) {
    const originalDocumentAttachments = state.document.documentAttachments;
    try {
      const documentAttachment: IDocumentAttachment[] = await AttachmentService.load(documentId);
      dispatch(EAssemblerAction.LoadDocumentAttachmentsSuccess, { documentAttachment });
    } catch (e) {
      dispatch(EAssemblerAction.LoadDocumentAttachmentsFailed, originalDocumentAttachments);
    }
  },
  [EAssemblerAction.LoadDocumentAttachmentsSuccess](
    { commit },
    { documentAttachment }: { documentAttachment: IDocumentAttachment[] }
  ) {
    commit(EAssemblerMutation.UpdateDocumentAttachment, documentAttachment);
  },
  [EAssemblerAction.LoadDocumentAttachmentsFailed](
    { commit, dispatch },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(EAssemblerMutation.UpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-load-attachments'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.UpdateRecipientsSigningOrder](
    { commit, dispatch, state },
    { newIndex, oldIndex, drag }: { newIndex: number; oldIndex: number; drag: boolean }
  ) {
    const oldRecipients: IRecipient[] = state.document.recipients;
    let sortedRecipients: IRecipient[] = RecipientHelper.arrangeRecipientSignOrder(
      newIndex,
      oldIndex,
      oldRecipients,
      drag
    );

    const hasApprover: boolean = state.document.recipients.some(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
    );

    if (hasApprover) {
      const lastApprover: IRecipient = [...sortedRecipients]
        .reverse()
        .find((recipient: IRecipient) => recipient.role === ERecipientRole.Approver);
      const lastSigner: IRecipient = [...sortedRecipients]
        .reverse()
        .find((recipient: IRecipient) => recipient.role === ERecipientRole.Signer);

      if (lastApprover.orderNum >= lastSigner.orderNum) {
        dispatch(
          `${alertNamespace}/${EAlertAction.ShowWarning}`,
          translate('approver-cant-be-last-in-order'),
          {
            root: true,
          }
        );
        const lastSignerIndex: number = sortedRecipients.findIndex(
          recipient => recipient.id === lastSigner.id
        );
        sortedRecipients = RecipientHelper.arrangeRecipientSignOrder(
          lastApprover.orderNum,
          lastSignerIndex,
          sortedRecipients,
          drag
        );
      }
    }

    const recipientOrder: Partial<IRecipient>[] = sortedRecipients.map((recipient: IRecipient) => ({
      id: recipient.id,
      orderNum: recipient.orderNum,
    }));

    commit(EAssemblerMutation.SetRecipients, sortedRecipients);
    try {
      await RecipientService.updateRecipientOrder(state.document.id, recipientOrder);
    } catch (error) {
      dispatch(EAssemblerAction.UpdateRecipientsSigningOrderFailed, oldRecipients);
    }
  },
  [EAssemblerAction.UpdateRecipientsSigningOrderFailed]({ commit }, oldRecipients: IRecipient[]) {
    commit(EAssemblerMutation.SetRecipients, oldRecipients);
  },
  async [EAssemblerAction.LoadRecipients]({ dispatch, state }) {
    try {
      const recipients: IRecipient[] = await RecipientService.loadRecipients(state.document.id);
      dispatch(EAssemblerAction.LoadRecipientsSuccess, recipients);
    } catch {
      dispatch(EAssemblerAction.LoadRecipientsFailed);
    }
  },
  [EAssemblerAction.LoadRecipientsSuccess]({ commit, dispatch }, recipients: IRecipient[]) {
    commit(EAssemblerMutation.SetRecipients, recipients);
    if (recipients.length === 1) {
      dispatch(EAssemblerAction.ConnectFirstRecipientToFields);
    }
  },
  [EAssemblerAction.LoadRecipientsFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-recipients'),
      {
        root: true,
      }
    );
  },
  [EAssemblerAction.LoadLegacyDocumentData]({ state, dispatch }) {
    dispatch(
      `${documentEditNamespace}/${EDocumentEditAction.LoadDocumentData}`,
      state.document.id,
      {
        root: true,
      }
    );
    dispatch(`${documentEditNamespace}/${EDocumentEditAction.InitializeDocumentEdit}`, null, {
      root: true,
    });
  },
  async [EAssemblerAction.DeleteDocument]({ dispatch }, id: string) {
    try {
      await DocumentService.delete(id);
      dispatch(EAssemblerAction.DeleteDocumentSuccess);
    } catch {
      dispatch(EAssemblerAction.DeleteDocumentFailed);
    }
  },
  [EAssemblerAction.DeleteDocumentSuccess]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate('document-successfully-deleted'),
      {
        root: true,
      }
    );
    dispatch(EAssemblerAction.ResetState);
  },
  [EAssemblerAction.DeleteDocumentFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-delete-document'),
      {
        root: true,
      }
    );
    dispatch(EAssemblerAction.ResetState);
  },
  async [EAssemblerAction.UpdateDocumentUser](
    { dispatch, commit, state },
    { documentId, user }: { documentId: string; user: IUser }
  ) {
    try {
      await DocumentService.updateDocument({ userId: user.id }, documentId);
      dispatch(`fields/${EFieldAction.LoadSenderSignature}`, user.id);
      commit(EAssemblerMutation.UpdateDocumentUser, user);
      if (state.selectedUser) {
        dispatch(EAssemblerAction.SetSelectedUser, user);
      }
    } catch {
      dispatch(EAssemblerAction.UpdateDocumentUserFailed);
    }
  },
  [EAssemblerAction.UpdateDocumentUserFailed]({ dispatch, commit }) {
    commit(EAssemblerMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-change-document-owner'),
      {
        root: true,
      }
    );
  },
  [EAssemblerAction.SortSigningRecipients]({ dispatch, state }) {
    state.document.recipients
      .filter((recipient: IRecipient) => recipient.role == 'signer')
      .forEach((recipient: IRecipient, i: number) => {
        dispatch(EAssemblerAction.UpdateRecipient, {
          id: recipient.id,
          properties: { orderNum: i + 1 },
        });
      });
  },
  async [EAssemblerAction.GetPreviewToken]({ commit, dispatch, state }) {
    try {
      const previewToken = await DocumentService.getPreviewToken(state.document.id);
      commit(EAssemblerMutation.SetPreviewToken, previewToken);
    } catch {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('failed-to-preview-document'),
        {
          root: true,
        }
      );
    }
  },
  async [EAssemblerAction.UpdateRecipientMobile](
    { dispatch, state },
    { id, mobile }: { id: string; mobile: string }
  ) {
    const oldRecipient: IRecipient = state.document.recipients.find(
      (recipient: IRecipient) => recipient.id === id
    );
    const updatedRecipient: IRecipient = { ...oldRecipient, ...{ mobile } };
    try {
      await RecipientService.updateRecipient(id, { mobile });
      dispatch(EAssemblerAction.UpdateRecipientMobileSuccess, updatedRecipient);
    } catch {
      dispatch(EAssemblerAction.UpdateRecipientFailed, oldRecipient);
    }
  },
  [EAssemblerAction.UpdateRecipientMobileSuccess]({ commit }, recipient: IRecipient) {
    commit(EAssemblerMutation.UpdateRecipient, recipient);
  },
  [EAssemblerAction.SetPages]({ commit }, pages: IDocumentPage[]) {
    const uniquePages: IDocumentPage[] = pages.reduce(
      (acc: IDocumentPage[], curr: IDocumentPage) => {
        const exists = !!acc.find((page: IDocumentPage) => page.id === curr.id);
        return exists ? acc : [...acc, curr];
      },
      []
    );
    commit(EAssemblerMutation.SetPages, uniquePages);
  },
  async [EAssemblerAction.LoadPageThumbUrl]({ commit, state }, pageId: string) {
    const { thumbUrl }: Partial<IDocumentPage> = await DocumentService.loadPageThumbUrl(
      pageId,
      state.document.id
    );
    const pages: IDocumentPage[] = state.document.pages.map((page: IDocumentPage) =>
      page.id === pageId
        ? {
            ...page,
            thumbUrl,
          }
        : page
    );
    commit(EAssemblerMutation.SetPages, pages);
  },
  async [EAssemblerAction.CreateDocument]({ dispatch }, documentType: string) {
    try {
      dispatch('setContentIsLoading', true, { root: true });

      const documentId: string = await DocumentService.createDocument(documentType);
      dispatch(EAssemblerAction.CreateDocumentSuccess, documentId);
    } catch (error) {
      dispatch(EAssemblerAction.CreateDocumentFailed, error);
    }
  },
  [EAssemblerAction.CreateDocumentSuccess]({ commit, dispatch }, documentId: string) {
    commit(EAssemblerMutation.SetCreateDocumentId, documentId);
    dispatch('setContentIsLoading', false, { root: true });
  },

  [EAssemblerAction.CreateDocumentFailed]({ dispatch }, error: any) {
    console.error(`Error while creating new document ${error}`);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-create-document'),
      {
        root: true,
      }
    );
    dispatch('setContentIsLoading', false, { root: true });
  },

  async [EAssemblerAction.SealDocument](
    { commit, dispatch },
    { documentId, sendDocument }: { documentId: string; sendDocument: boolean }
  ) {
    try {
      await DocumentService.sealDocument(documentId, sendDocument);
      commit(EAssemblerMutation.SetSealValidationSuccess, true);
    } catch (error) {
      dispatch(EAssemblerAction.SealValidationFailed);
    }
  },
  [EAssemblerAction.SealValidationFailed]({ commit, dispatch }) {
    commit(EAssemblerMutation.SetSealValidationSuccess, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-validate-document'),
      {
        root: true,
      }
    );
  },
  [EAssemblerAction.SealDocumentFailed]({ commit, dispatch }) {
    commit(EAssemblerMutation.SetSealValidationSuccess, null);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('failed-to-seal-document'), {
      root: true,
    });
  },
  async [EAssemblerAction.DeleteTemplate]({ dispatch }, id: string) {
    try {
      await DocumentService.deleteTemplate(id);
    } catch (error) {
      dispatch(EAssemblerAction.DeleteTemplateFailed);
    }
  },
  [EAssemblerAction.DeleteTemplateFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-delete-template'),
      {
        root: true,
      }
    );
  },
  [EAssemblerAction.SetSidebarCollapsed]({ commit }, sidebarCollapsed: boolean) {
    commit(EAssemblerMutation.SetSidebarCollapsed, sidebarCollapsed);
  },
  async [EAssemblerAction.SaveSignatureBase64]({ dispatch }, signature: string[]) {
    const mappedSignature = `data:${signature.join(',')}`;
    try {
      await DocumentService.updateSignature(mappedSignature);
    } catch (error) {
      dispatch(EAssemblerAction.SaveSignatureBase64Failed, error);
    }
  },
  [EAssemblerAction.SaveSignatureBase64Failed](_, error: any) {
    console.error('Could not save signature in base64', error);
  },
  async [EAssemblerAction.AssignFieldToRecipient]({ dispatch, state }, recipientId: string) {
    try {
      await RecipientService.assignFieldToRecipient(state.document.id, recipientId);
    } catch {
      dispatch(EAssemblerAction.AssignFieldToRecipientFailed);
    }
  },
  [EAssemblerAction.AssignFieldToRecipientFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-connect-fields-to-recipient'),
      {
        root: true,
      }
    );
  },
  [EAssemblerAction.AddEngageVideo]({ commit }, engageVideo: IEngagementVideo) {
    commit(EAssemblerMutation.SetEngageVideo, engageVideo);
  },
  async [EAssemblerAction.RemoveEngageVideo](
    { commit, dispatch },
    { videoId, documentId }: { videoId: string; documentId: string }
  ) {
    commit(EAssemblerMutation.SetEngageVideo, null);
    try {
      await VideoService.removeEngageVideo(videoId, documentId);
    } catch (e) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-remove-engagement-video').toString(),
        {
          root: true,
        }
      );
    }
  },
  [EAssemblerAction.SetActiveContentSelector]({ commit }, activeContentSelector: number) {
    commit(EAssemblerMutation.SetActiveContentSelector, activeContentSelector);
  },
  async [EAssemblerAction.LoadDocumentResources]({ dispatch, state }) {
    try {
      const documentResources: IDocumentResource[] = await ResourceService.loadDocumentResources({
        documentId: state.document.id,
      });
      dispatch(EAssemblerAction.LoadDocumentResourcesSuccess, documentResources);
    } catch (e) {
      dispatch(EAssemblerAction.LoadDocumentResourcesFailed);
    }
  },
  [EAssemblerAction.LoadDocumentResourcesSuccess](
    { commit, state },
    resources: IDocumentResource[]
  ) {
    const pages: IDocumentPage[] = state.document?.pages || [];
    if (!pages.length) {
      return;
    }
    const mappedPages: IDocumentPage[] = AssemblerHelper.mapDocumentResources(pages, resources);
    commit(EAssemblerMutation.SetPages, mappedPages);
  },
  [EAssemblerAction.LoadDocumentResourcesFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-fetch-document-resources'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.DisconnectResource]({ dispatch }, { documentId, resourceId }) {
    try {
      const payload: IImplementResourceDTO = { documentId, resourceId };
      await ResourceService.disconnect(payload);
    } catch (error) {
      dispatch(EAssemblerAction.DisconnectResourceFailed, error);
    }
  },
  [EAssemblerAction.DisconnectResourceFailed](_, error) {
    console.error('Could not disconnect resource', error);
  },
  [EAssemblerAction.UpdatePage]({ state, commit }, updatedPage: IDocumentPage) {
    commit(
      EAssemblerMutation.SetPages,
      DocumentManipulatorHelper.updatePage(updatedPage, state.document.pages)
    );
  },
  async [EAssemblerAction.AddPages]({ commit, state, dispatch }, pages: IDocumentPage[]) {
    dispatch(EAssemblerAction.SetActiveContentSelector, null);
    const mappedPages: IDocumentPage[] = DocumentManipulatorHelper.insertNewPages(
      pages,
      state.document.pages
    );
    commit(EAssemblerMutation.SetPages, mappedPages);
    await dispatch(`fields/${EFieldAction.Load}`, state.document.id);
  },
  async [EAssemblerAction.AddRecipientRole](
    { dispatch, state },
    { payload }: { payload: IRecipient }
  ) {
    try {
      await RecipientService.addRecipient(payload);
      await dispatch(EAssemblerAction.LoadRecipients);
      const recipients: IRecipient[] = AssemblerHelper.sortRecipients(state.document.recipients);
      dispatch(EAssemblerAction.AddRecipientRoleSuccess, { recipients });
    } catch (e) {
      alert(e);
      dispatch(EAssemblerAction.AddRecipientRoleFailed);
    }
  },
  async [EAssemblerAction.AddRecipientRoleSuccess](
    { dispatch, state },
    { recipients }: { recipients: IRecipient[] }
  ) {
    try {
      const recipientOrder: Partial<IRecipient>[] = AssemblerHelper.orderRecipients(recipients);
      await RecipientService.updateRecipientOrder(state.document.id, recipientOrder);
      await dispatch(EAssemblerAction.LoadRecipients);
    } catch {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-update-recipient-order'),
        {
          root: true,
        }
      );
    }
  },
  [EAssemblerAction.AddRecipientRoleFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-add-template-role'),
      {
        root: true,
      }
    );
  },
  async [EAssemblerAction.UpdateRecipientRole](
    { dispatch },
    { recipient }: { recipient: IRecipient }
  ) {
    await RecipientService.addRecipient(recipient);
    await dispatch(EAssemblerAction.LoadRecipients);
  },
  [EAssemblerAction.ShowAddRoleModal]({ commit }, show: boolean) {
    commit(EAssemblerMutation.ShowAddRoleModal, show);
  },
  async [EAssemblerAction.AddGoogleDocsPages]({ commit, state, dispatch }, pages: IDocumentPage[]) {
    commit(EAssemblerMutation.SetPages, pages);
    await dispatch(`fields/${EFieldAction.Load}`, state.document.id);
  },
  async [EAssemblerAction.AddDocument]({ commit, dispatch, state }, document: IDocument) {
    commit(EAssemblerMutation.SetDocument, document);
    dispatch(EAssemblerAction.SetActiveContentSelector, null);
    await dispatch(`fields/${EFieldAction.Load}`, state.document.id);
    await dispatch(EAssemblerAction.LoadDocumentResources);
    await dispatch(EAssemblerAction.ConnectFirstRecipientToFields);
    await dispatch(EAssemblerAction.LoadLegacyDocumentData);
    if (state.currentDocumentPage) {
      return;
    }
    const currentDocumentPage: IDocumentPage = document.pages.find(
      (page: IDocumentPage) => page.pageType === EDocumentPageType.DocumentContent
    );
    dispatch(EAssemblerAction.SetCurrentDocumentPage, currentDocumentPage);
  },
  [EAssemblerAction.ReplacePage]({ commit, dispatch, state }, page: IDocumentPage) {
    commit(EAssemblerMutation.SetPageToBeReplaced, page);
    const updatedPages: IDocumentPage[] = DocumentManipulatorHelper.filterPage(
      page,
      state.document.pages
    );
    dispatch(EAssemblerAction.SetPages, updatedPages);
    dispatch(EAssemblerAction.SetActiveContentSelector, page.pageNum - 1);
  },
  [EAssemblerAction.CancelReplacePage]({ dispatch, state }) {
    const updatedPages: IDocumentPage[] = DocumentManipulatorHelper.insertNewPages(
      [state.pageToBeReplaced],
      [...state.document?.pages]
    );
    dispatch(EAssemblerAction.SetPages, updatedPages);
    dispatch(EAssemblerAction.ResetReplacePage);
  },
  [EAssemblerAction.ResetReplacePage]({ commit }) {
    commit(EAssemblerMutation.SetPageToBeReplaced, { ...getDefaultState() }.pageToBeReplaced);
  },
  async [EAssemblerAction.LoadExternalEditorId]({ dispatch }, documentId: string) {
    try {
      const externalEditorId: string = await DocumentService.loadExternalEditorId(documentId);
      dispatch(EAssemblerAction.LoadExternalEditorIdSuccess, externalEditorId);
    } catch {
      dispatch(EAssemblerAction.LoadExternalEditorIdFailed);
    }
  },
  [EAssemblerAction.LoadExternalEditorIdSuccess]({ commit, state }, externalEditorId: string) {
    commit(EAssemblerMutation.SetDocument, { ...state.document, externalEditorId });
  },
  [EAssemblerAction.LoadExternalEditorIdFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate(couldNotUpdateMessage), {
      root: true,
    });
  },
  async [EAssemblerAction.AddTag]({ dispatch, commit, state }, tag: ITag) {
    const oldTags: ITag[] = state.document.tags;
    const tags: ITag[] = [...state.document.tags, tag];
    commit(EAssemblerMutation.SetDocumentTags, tags);
    try {
      await TagService.addMultiple([state.document.id], [tag.id]);
      await TagService.legacyUpdateDocument(state.document.id, tags);
    } catch (error) {
      dispatch(EAssemblerAction.TagFailed, {
        tags: oldTags,
        message: translate('could-not-add-tag'),
      });
    }
  },
  async [EAssemblerAction.CreateTag]({ dispatch }, title: string) {
    try {
      const createdTag: ITag = await TagService.create(title);
      dispatch(`${tagsModuleName}/${ETagAction.CreateSuccess}`, createdTag, { root: true });
      dispatch(EAssemblerAction.AddTag, createdTag);
    } catch (error) {
      dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-create-tag'), {
        root: true,
      });
    }
  },
  async [EAssemblerAction.RemoveTag]({ dispatch, state, commit }, tag: ITag) {
    const oldTags: ITag[] = state.document.tags;
    const tags: ITag[] = state.document.tags.filter((oldTag: ITag) => oldTag.id !== tag.id);
    commit(EAssemblerMutation.SetDocumentTags, tags);
    try {
      await TagService.removeMultiple([state.document.id], [tag.id]);
      await TagService.legacyUpdateDocument(state.document.id, tags);
    } catch (error) {
      dispatch(EAssemblerAction.TagFailed, {
        tags: oldTags,
        message: translate('could-not-remove-tag'),
      });
    }
  },
  [EAssemblerAction.TagFailed](
    { dispatch, commit },
    { tags, message }: { tags: ITag[]; message: string }
  ) {
    commit(EAssemblerMutation.SetDocumentTags, tags);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, message, {
      root: true,
    });
  },
  async [EAssemblerAction.UpdateEngagementSection](
    { dispatch, state, commit },
    payload: IUpdateDocument
  ) {
    const oldDocument: IDocument = state.document;
    const updatedDocument: IDocument = { ...state.document, ...payload };
    commit(EAssemblerMutation.SetDocument, updatedDocument);
    try {
      await DocumentService.updateDocument(payload, state.document.id);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(EAssemblerAction.UpdateEngagementSectionFailed, oldDocument);
    }
  },
  [EAssemblerAction.UpdateEngagementSectionFailed]({ dispatch, commit }, document: IDocument) {
    commit(EAssemblerMutation.SetDocument, document);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate(couldNotUpdateMessage), {
      root: true,
    });
  },
};
